import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Code, Mail } from 'lucide-react';
import ProjectCard from './ProjectCard';
import { PROJECTS, SKILLS } from '../constants';
import { Section } from '../types';
import ContactService from '../services/ContactService';

interface SectionContentProps {
  activeSection: Section;
  previousSection: Section;
  onSectionChange: (section: Section) => void;
}

const SectionContent: React.FC<SectionContentProps> = ({ activeSection, previousSection, onSectionChange }) => {
  const navigate = useNavigate();
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactIsSubmitting, setContactIsSubmitting] = useState(false);
  const [contactStatus, setContactStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (contactIsSubmitting) return;

    try {
      console.log('[Contact] submit:click', {
        name: contactName,
        email: contactEmail,
        messageLen: contactMessage.length,
      });

      setContactIsSubmitting(true);
      setContactStatus('idle');

      const timeoutMs = 15000;
      await Promise.race([
        ContactService.submit({
          name: contactName.trim(),
          email: contactEmail.trim(),
          message: contactMessage.trim(),
        }),
        new Promise<void>((_, reject) =>
          setTimeout(() => reject(new Error(`Contact submit timed out after ${timeoutMs}ms`)), timeoutMs)
        )
      ]);

      console.log('[Contact] submit:done');

      setContactName('');
      setContactEmail('');
      setContactMessage('');
      setContactStatus('success');
    } catch (err) {
      console.error('[Contact] submit:error', err);
      setContactStatus('error');
    } finally {
      setContactIsSubmitting(false);
    }
  };

  switch (activeSection) {
    case Section.Home:
      return (
        <div className="min-h-full flex flex-col justify-center max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-700 py-20 md:py-0">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] font-bold tracking-widest uppercase mb-6 w-fit">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
            <span>Available for Hire</span>
          </div>
          <h1 className="text-5xl md:text-8xl font-black tracking-tighter text-white mb-6 md:mb-8 leading-[1] md:leading-[0.9]">
            SANKET <span className="text-zinc-800 outline-text">DEVELOPER</span>
          </h1>
          <p className="text-lg md:text-xl text-zinc-400 leading-relaxed mb-8 md:mb-10 max-w-lg font-light">
            Building high-performance <span className="text-white font-medium">digital products</span> that bridge complex logic with elegant design.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 pb-12 md:pb-0">
            <button
              onClick={() => onSectionChange(Section.Work)}
              className="group px-8 md:px-10 py-4 bg-white text-black rounded-full font-bold transition-all flex items-center justify-center space-x-3 hover:scale-105"
            >
              <span>Recent Projects</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            {/* <button className="px-8 md:px-10 py-4 bg-transparent hover:bg-white/5 text-white rounded-full font-bold transition-all border border-white/10 flex items-center justify-center space-x-3">
              <Download className="w-4 h-4 text-red-500" />
              <span>Resume</span>
            </button> */}
          </div>
        </div>
      );

    case Section.Work:
      return (
        <div className="h-full py-8 md:py-12 animate-in fade-in duration-500 relative pb-32 md:pb-12">
          <>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 md:mb-16 space-y-4 md:space-y-0">
              <div>
                <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-white mb-2 uppercase">Works</h2>
                <p className="text-zinc-500 font-medium">Selected full-stack cases.</p>
              </div>
              <div className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest flex items-center space-x-3">
                <span>({String(PROJECTS.length).padStart(2, '0')}) Projects Total</span>
                <div className="hidden md:block w-16 h-[1px] bg-zinc-800"></div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {PROJECTS.map(project => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onClick={() => navigate(`/work/${project.id}`)}
                />
              ))}
            </div>
          </>
        </div>
      );

    case Section.Skills:
      return (
        <div className={`min-h-full py-12 md:py-20 animate-in fade-in duration-500 pb-32 md:pb-12 ${previousSection === Section.About ? 'slide-in-from-top-4' : ''}`}>
          <div className="mb-10 md:mb-16">
            <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-white mb-2 uppercase">Stack</h2>
            <p className="text-zinc-500 font-medium">My professional toolset.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {SKILLS.map(skill => (
              <div key={skill.category} className="group p-6 md:p-10 rounded-3xl md:rounded-[2.5rem] bg-zinc-900/40 border border-white/5 hover:border-red-500/20 transition-all duration-500 glass-panel relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 text-white/5 group-hover:text-red-500/10 transition-colors">
                  <Code className="w-12 md:w-16 h-12 md:h-16" />
                </div>
                <h3 className="text-red-500 text-[10px] font-bold tracking-[0.3em] uppercase mb-6 md:mb-10">{skill.category}</h3>
                <ul className="space-y-4 md:space-y-5">
                  {skill.items.map(item => (
                    <li key={item} className="flex items-center space-x-3 md:space-x-4 text-zinc-300 group/item">
                      <div className="w-1.5 h-1.5 rounded-full bg-zinc-700 group-hover/item:bg-red-500 transition-colors"></div>
                      <span className="font-semibold text-base md:text-lg tracking-tight group-hover/item:text-white transition-colors">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      );

    case Section.About:
      return (
        <div className={`min-h-full flex flex-col justify-center max-w-4xl animate-in fade-in duration-500 py-12 md:py-20 pb-32 md:pb-12 ${previousSection === Section.Skills ? 'slide-in-from-bottom-4' : ''}`}>
          <div className="inline-flex items-center space-x-2 text-zinc-600 text-[10px] font-bold tracking-widest uppercase mb-6 md:mb-8">
            <span>The Story</span>
            <div className="w-8 md:w-12 h-[1px] bg-zinc-800"></div>
          </div>
          <h2 className="text-4xl md:text-7xl font-black tracking-tighter text-white mb-8 md:mb-10 leading-[1] md:leading-[0.9] uppercase">
            Blending code <br /> & <span className="text-red-500 italic">Creativity.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 text-zinc-400 text-base md:text-lg leading-relaxed font-light">
            <p>
              Based in Mumbai, I specialize in bridging the gap between design and development. I enjoy building high-performance web applications that ship fast and feel polished.
            </p>
            <p>
              I thrive in fast-paced startup environments where ownership is high and problems are ambiguous. My goal is always to deliver software that is efficient under the hood and beautiful on the surface.
            </p>
          </div>
          {/* <div className="mt-10 md:mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-8">
            <div className="flex -space-x-4">
              {[1, 2, 3, 4].map(i => (
                <img key={i} className="w-12 md:w-14 h-12 md:h-14 rounded-full border-[3px] border-zinc-950 grayscale hover:grayscale-0 transition-all" src={`https://picsum.photos/seed/${i + 20}/100/100`} alt="Avatar" />
              ))}
            </div>
            <div>
              <p className="text-white font-bold text-base md:text-lg">Trusted Globally</p>
              <p className="text-xs md:text-sm text-zinc-500">Worked with teams in 8+ countries.</p>
            </div>
          </div> */}
        </div>
      );

    case Section.Contact:
      return (
        <div className="min-h-full flex items-center justify-center animate-in fade-in duration-500 py-12 md:py-20 pb-40 md:pb-0">
          <div className="w-full max-w-2xl p-8 md:p-12 rounded-3xl md:rounded-[3rem] glass-panel border border-white/5 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 -mt-10 -mr-10 p-12 md:p-20 text-zinc-800 opacity-10">
              <Mail className="w-32 md:w-48 h-32 md:h-48" />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tighter relative z-10 uppercase">Let's talk.</h2>
            <p className="text-zinc-500 text-base md:text-lg mb-8 md:mb-10 relative z-10 max-w-md">Ready to build something exceptional?</p>

            <form className="space-y-4 md:space-y-6 relative z-10" onSubmit={handleContactSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div className="space-y-1 md:space-y-2">
                  <label className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest ml-1">Name</label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    className="w-full bg-zinc-900/50 border border-white/5 rounded-xl md:rounded-2xl px-4 md:px-5 py-3 md:py-4 text-white focus:outline-none focus:border-red-500/50 transition-all font-medium"
                    placeholder="Your name"
                  />
                </div>
                <div className="space-y-1 md:space-y-2">
                  <label className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest ml-1">Email</label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="w-full bg-zinc-900/50 border border-white/5 rounded-xl md:rounded-2xl px-4 md:px-5 py-3 md:py-4 text-white focus:outline-none focus:border-red-500/50 transition-all font-medium"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              <div className="space-y-1 md:space-y-2">
                <label className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest ml-1">Your Project</label>
                <textarea
                  rows={3}
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  className="w-full bg-zinc-900/50 border border-white/5 rounded-xl md:rounded-2xl px-4 md:px-5 py-3 md:py-4 text-white focus:outline-none focus:border-red-500/50 transition-all resize-none font-medium"
                  placeholder="Tell me what you're building..."
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={contactIsSubmitting}
                className="group w-full py-4 md:py-5 bg-red-600 text-white font-black rounded-xl md:rounded-2xl hover:bg-red-700 transition-all flex items-center justify-center space-x-3 text-base md:text-lg tracking-tight disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span>{contactIsSubmitting ? 'SENDING…' : 'SEND'}</span>
              </button>

              {contactStatus === 'success' && (
                <p className="text-[10px] font-bold tracking-widest uppercase text-emerald-500">Submitted — I’ll get back to you soon.</p>
              )}
              {contactStatus === 'error' && (
                <p className="text-[10px] font-bold tracking-widest uppercase text-red-500">Something went wrong — please try again.</p>
              )}
            </form>
          </div>
        </div>
      );
  }
};

export default SectionContent;
